<template>
  <div>
    <b-row v-if="task !== null">
      <b-col cols="12">
        <!-- Payment Details -->
        <b-row class="mt-2 mb-4">
          <b-col cols="12">
            <PRNViewer :tracking-number="task.data.trackingNumber || task.data.applicationId" />
          </b-col>
        </b-row>

        <b-row class="mx-auto my-2">
          <b-col cols="12 p-0">
            <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2">Copyright Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Tracking Number</th>
                <th class="px-2 py-1">Title</th>
                <th class="px-2 py-1">Description</th>
                <th class="px-2 py-1">Is Derivative</th>
                <th class="px-2 py-1">Status</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" >
                <td class="px-2 py-1 font-bold text-gray">
                    <span class="">{{ task.data.trackingNumber || task.data.applicationId }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ task.data.title || (task.data.workTypes && task.data.workTypes[0] ? task.data.workTypes[0].name : '') }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ getDescription(task.data.description) || (task.data.workTypes && task.data.workTypes[0] ? task.data.workTypes[0].description : '') }}</span>
                </td>
                <td class="px-2 py-1">
                  <span class="text-gray">{{ isDerivative(task.data.isDerivativeWork) }}</span>
                </td>
                <td class="px-2 py-1">
                  <span :class="statusColorCode(task.data.status)">{{statusDataInfo(task.data.status)}}</span>
                </td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
 
        <!-- Work Types -->
        <b-row class="mt-2 mb-4" v-if="task.data.workTypes && task.data.workTypes.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Work Types</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Description</th>
                <th class="px-2 py-1">Sub Work Type</th>
                <th class="px-2 py-1">Sub Work Type Description</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(wt, idx) in task.data.workTypes" :key="idx">
                <td class="px-2 py-1">{{wt.name}}</td>
                <td class="px-2 py-1">{{wt.description}}</td>
                <td class="px-2 py-1">{{wt.subWorkType ? wt.subWorkType.name : 'N/A'}}</td>
                <td class="px-2 py-1">{{wt.subWorkType ? wt.subWorkType.description : 'N/A'}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>
 
        <!-- Ownerships -->
        <b-row class="mt-2 mb-4" v-if="task.data.ownerships && task.data.ownerships.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Ownerships / Rights Holders</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Type</th>
                <th class="px-2 py-1">ID Number</th>
                <th class="px-2 py-1">Is Owner</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(owner, idx) in task.data.ownerships" :key="idx">
                <td class="px-2 py-1 text-capitalize">
                  {{ owner.holderName || (owner.individual ? (owner.individual.name || (owner.individual.firstName ? owner.individual.firstName + ' ' + (owner.individual.lastName || '') : '')) : (owner.entity ? (owner.entity.entityName || owner.entity.registeredName || owner.entity.name) : '')) }}
                </td>
                <td class="px-2 py-1 text-capitalize">
                  {{ (owner.holderType || (owner.individual ? 'INDIVIDUAL' : (owner.entity ? 'CORPORATE' : ''))).toLowerCase() }}
                </td>
                <td class="px-2 py-1">
                  {{ owner.idNumber || (owner.individual ? (owner.individual.nationalId || owner.individual.idNumber) : (owner.entity ? (owner.entity.entityNumber || owner.entity.brn || owner.entity.registrationNumber) : '')) }}
                </td>
                <td class="px-2 py-1">
                  {{ owner.isOwner ? 'Yes' : 'No' }}
                </td>
              </tr>
              </tbody>
            </table>
            <h6 class="font-bold my-2 mt-3">Ownership Details</h6>
            <LegalPersons :persons="mappedOwnerships" />
          </b-col>
        </b-row>

        <!-- Claimants -->
        <b-row class="mt-2 mb-4" v-if="task.data.claimants && task.data.claimants.length > 0">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-gray"
                   style="border: solid 1px #dedede;">
              <thead>
              <tr class="bg-gray-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Claimants</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Type</th>
                <th class="px-2 py-1">Type of Claim</th>
                <th class="px-2 py-1">Email</th>
                <th class="px-2 py-1">Phone Number</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #dedede;" v-for="(claimant, idx) in task.data.claimants" :key="idx">
                <td class="px-2 py-1 text-capitalize">
                  {{ claimant.individual ? (claimant.individual.name || (claimant.individual.firstName ? claimant.individual.firstName + ' ' + (claimant.individual.lastName || '') : '')) : (claimant.entity ? (claimant.entity.entityName || claimant.entity.registeredName || claimant.entity.name) : '') }}
                </td>
                <td class="px-2 py-1 text-capitalize">
                  {{ claimant.claimantType ? claimant.claimantType.toLowerCase() : '' }}
                </td>
                <td class="px-2 py-1 text-capitalize">
                  {{ claimant.typeOfClaim ? claimant.typeOfClaim.toLowerCase() : '' }}
                </td>
                <td class="px-2 py-1">
                  {{ claimant.individual ? claimant.individual.email : (claimant.entity ? (claimant.entity.primaryEmail || claimant.entity.email) : '') }}
                </td>
                <td class="px-2 py-1">
                  {{ claimant.individual ? claimant.individual.phoneNumber : (claimant.entity ? (claimant.entity.primaryPhone || claimant.entity.entityPhone || claimant.entity.phoneNumber) : '') }}
                </td>
              </tr>
              </tbody>
            </table>
            <h6 class="font-bold my-2 mt-3">Claimant Details</h6>
            <LegalPersons :persons="mappedClaimants" />
          </b-col>
        </b-row>
 
        <!-- Applicant Details (Fallback) -->
        <b-row class="mt-2 mb-4" v-if="task.data.applicant">
          <b-col cols="12">
            <table class="min-w-full leading-normal mt-1 text-blue-dark"
                   style="border: solid 1px #bdd7f1;">
              <thead>
              <tr class="bg-blue-pale">
                <td colspan="12" class="px-2 py-2 font-bold">Applicant Details</td>
              </tr>
              <tr>
                <th class="px-2 py-1">Name</th>
                <th class="px-2 py-1">Email</th>
                <th class="px-2 py-1">Phone</th>
              </tr>
              </thead>
              <tbody>
              <tr style="border: solid 1px #bdd7f1;">
                <td class="px-2 py-1 text-capitalize">{{task.data.applicant.givenName || task.data.applicant.firstName}} {{task.data.applicant.surname || task.data.applicant.lastName}}</td>
                <td class="px-2 py-1">{{task.data.applicant.email}}</td>
                <td class="px-2 py-1">{{task.data.applicant.phoneNumber}}</td>
              </tr>
              </tbody>
            </table>
          </b-col>
        </b-row>

      </b-col>
    </b-row>
  </div>
</template>
 
<script>
import moment from "moment";
import PRNViewer from "@/views/home/commons/PRNViewer.vue";
import LegalPersons from "@/views/home/commons/LegalPersons";
 
export default {
  name: "CopyrightApplication",
  components: { PRNViewer, LegalPersons },
  props:{
    task: {
      type: [Object, null],
      default: null,
    }
  },
  data(){
    return{
      hasComments: false,
      currentRoute: "form",
      selectedId:null,
      margin:"2px",
      hasContent:false,
    }
  },
  methods:{
    isDerivative(val) {
      if (typeof val === 'object' && val !== null) {
        return val.Valid && val.Bool ? 'Yes' : 'No';
      }
      return val ? 'Yes' : 'No';
    },
    getDescription(val) {
      if (typeof val === 'object' && val !== null) {
        return val.Valid ? val.String : '';
      }
      return val || '';
    },
    statusDataInfo(status) {
      if (!status) return "";
      if (status.toUpperCase() === "APPROVED") {
        return "Approved"
      } else if (status.toUpperCase() === "RESERVED") {
        return "Reserved"
      } else if (status.toUpperCase() === "QUERIED") {
        return "Issues Raised"
      } else if (status.toUpperCase() === "ASSIGNED") {
        return "Being Processed"
      } else if (status.toUpperCase() === "PENDING") {
        return "Submitted"
      } else if (status.toUpperCase() === "APPEALED") {
        return "Appealed"
      }else if (status.toUpperCase() === "REJECTED") {
        return "Rejected"
      }else if (status.toUpperCase() === "DRAFT") {
        return "Draft"
      } else if (status.toUpperCase() === "AWAITING_PAYMENT_CONFIRMATION") {
        return "Confirming Payment"
      }else if (status.toUpperCase() === "PAYMENT_FAILED") {
        return "Payment Failed"
      }
      return status;
    },
    statusColorCode(status) {
      if (!status) return "";
      if (status.toUpperCase() === "APPROVED" || status.toUpperCase() === "RESERVED") {
        return "text-green"
      } else if (status.toUpperCase() === "QUERIED") {
        return "text-orange"
      } else if (status.toUpperCase() === "ASSIGNED") {
        return "text-blue"
      } else if (status.toUpperCase() === "PENDING") {
        return "text-gray"
      } else if (status.toUpperCase() === "APPEALED" ) {
        return "text-red"
      }else if (status.toUpperCase() === "REJECTED") {
        return "text-red"
      }else if (status.toUpperCase() === "DRAFT") {
        return "text-gray"
      } else if (status.toUpperCase() === "AWAITING_PAYMENT_CONFIRMATION") {
        return "text-purple"
      }else if (status.toUpperCase() === "PAYMENT_FAILED") {
        return "text-red"
      }
      return "";
    },
    dateFormat(date){
      return moment(date).format("DD-MM-YYYY")
    },
  },
  computed: {
    mappedClaimants() {
      if (!this.task || !this.task.data || !this.task.data.claimants) {
        return [];
      }
      return this.task.data.claimants.map(c => {
        const ind = c.individual || {};
        const ent = c.entity || {};
        const type = c.claimantType || c.holderType || '';
        const isCorporate = type === 'NON_INDIVIDUAL' || type === 'CORPORATE';
        return {
          entityType: isCorporate ? 'CORPORATE' : 'INDIVIDUAL',
          individual: {
            givenName: ind.givenName || ind.firstName || '',
            middleName: ind.middleName || '',
            surName: ind.surname || ind.lastName || ind.name || '',
            idType: ind.typeOfId || ind.idType || 'PASSPORT',
            idNumber: ind.idNumber || ind.nationalId || '',
            nationality: ind.nationality || '',
            email: ind.email || '',
            phoneNumber: ind.phoneNumber || '',
            sex: ind.gender || '',
            dateOfBirth: ind.dateOfBirth || '',
            occupation: ind.occupation || '',
            leaving: !ind.isAlive,
            address: {
              physicalAddress: ind.village ? {
                region: ind.village.region || null,
                district: ind.village.district || null,
                subcounty: ind.village.subcounty || null,
                parish: ind.village.parish || null,
                village: ind.village.village || null
              } : (ind.physicalAddress && typeof ind.physicalAddress === 'object' ? ind.physicalAddress : null),
              postalAddress: ind.postalAddress || ind.foreignPostalAddress || '',
              street: ind.street || '',
              plot: ind.plot || '',
              description: ind.foreignPhysicalAddress || ''
            }
          },
          corporate: {
            registeredName: ent.registeredName || ent.entityName || ent.name || c.holderName || '',
            brn: ent.brn || ent.entityNumber || ent.registrationNumber || c.idNumber || '',
            primaryEmail: ent.primaryEmail || ent.email || '',
            primaryPhone: ent.primaryPhone || ent.entityPhone || ent.phoneNumber || '',
            dateOfIncorporation: ent.dateOfIncorporation || '',
            subType: ent.subType || ent.entitySubType || '',
            countryOfOrigin: ent.countryOfOrigin || ent.country || '',
            addressInCountryOfIncorporation: ent.addressInCountryOfIncorporation || '',
            registeredAddress: ent.registeredAddress || (ent.postalAddress || ent.plot || ent.street || ent.entityAddress || ent.description ? {
              postalAddress: ent.postalAddress || '',
              plot: ent.plot || '',
              street: ent.street || '',
              description: ent.description || ent.entityAddress || ''
            } : null)
          }
        };
      });
    },
    mappedOwnerships() {
      if (!this.task || !this.task.data || !this.task.data.ownerships) {
        return [];
      }
      return this.task.data.ownerships.map(o => {
        const ind = o.individual || {};
        const ent = o.entity || {};
        const rawHolderType = o.holderType || (o.individual ? 'INDIVIDUAL' : (o.entity ? 'CORPORATE' : ''));
        const isCorporate = rawHolderType === 'NON_INDIVIDUAL' || rawHolderType === 'CORPORATE';
        return {
          entityType: isCorporate ? 'CORPORATE' : 'INDIVIDUAL',
          individual: {
            givenName: ind.givenName || ind.firstName || '',
            middleName: ind.middleName || '',
            surName: ind.surname || ind.lastName || ind.name || '',
            idType: ind.typeOfId || ind.idType || 'PASSPORT',
            idNumber: ind.idNumber || ind.nationalId || '',
            nationality: ind.nationality || '',
            email: ind.email || '',
            phoneNumber: ind.phoneNumber || '',
            sex: ind.gender || '',
            dateOfBirth: ind.dateOfBirth || '',
            occupation: ind.occupation || '',
            leaving: !ind.isAlive,
            address: {
              physicalAddress: ind.village ? {
                region: ind.village.region || null,
                district: ind.village.district || null,
                subcounty: ind.village.subcounty || null,
                parish: ind.village.parish || null,
                village: ind.village.village || null
              } : (ind.physicalAddress && typeof ind.physicalAddress === 'object' ? ind.physicalAddress : null),
              postalAddress: ind.postalAddress || ind.foreignPostalAddress || '',
              street: ind.street || '',
              plot: ind.plot || '',
              description: ind.foreignPhysicalAddress || ''
            }
          },
          corporate: {
            registeredName: ent.registeredName || ent.entityName || ent.name || o.holderName || '',
            brn: ent.brn || ent.entityNumber || ent.registrationNumber || o.idNumber || '',
            primaryEmail: ent.primaryEmail || ent.email || '',
            primaryPhone: ent.primaryPhone || ent.entityPhone || ent.phoneNumber || '',
            dateOfIncorporation: ent.dateOfIncorporation || '',
            subType: ent.subType || ent.entitySubType || '',
            countryOfOrigin: ent.countryOfOrigin || ent.country || '',
            addressInCountryOfIncorporation: ent.addressInCountryOfIncorporation || '',
            registeredAddress: ent.registeredAddress || (ent.postalAddress || ent.plot || ent.street || ent.entityAddress || ent.description ? {
              postalAddress: ent.postalAddress || '',
              plot: ent.plot || '',
              street: ent.street || '',
              description: ent.description || ent.entityAddress || ''
            } : null)
          }
        };
      });
    }
  }}
</script>

<style scoped>
th, td{
  font-size: 12px;
}
</style>
